import {
  getHeaders,
  history,
  showAlertSnackBar,
  clearUserDataAndLogout,
} from 'utils/Global';
import { ResponseType } from 'business/models/Models';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { API_RETRY_BASE_DELAY_MS, API_RETRY_COUNT } from 'config';
// import urlConst from 'constants/urlConst';
import { CacheHelper } from './CacheHelper';
import NetworkError from './NetworkError';
const cache = {};
let refreshTokenPromise = null;
export function ClearCache(url) {
  cache[url] = undefined;
}

const retryableStatusCodes = [408, 502, 503, 504];
function shouldRetry(errorOrResponse) {
  if (errorOrResponse instanceof Response) {
    return retryableStatusCodes.includes(errorOrResponse.status);
  }
  return errorOrResponse.message === 'Failed to fetch';
}

// fetcher is simple function without hooks for situations where we dont need component refresh
async function baseFetcher(
  url,
  method,
  body,
  asJson = false,
  forceFetch = false,
  signal = null,
) {
  if (cache[url] && !forceFetch) {
    return cache[url].clone();
  }

  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetchData(url, body, method, signal);

      if (response.status === 200) {
        if (method !== 'POST') {
          cache[url] = await response.clone();
        }
        if (asJson) {
          const responseJson = await response.json();
          resolve(responseJson);
        }
        resolve(response);
      } else if (response.status === 204) {
        resolve(response);
      } else if (response.status === 401) {
        // IS-TOKEN-EXPIRED is set in headers from sever side if authorization is failed
        // we call refresh token to get latest access token.
        // refreshTokenPromise is null intially and whenever refresh is being called , promise is assigned in it
        // so if parallel calls are made, they dont call refresh again, they use already assigned refresh promise
        const isTokenExpired = response.headers.get('IS-TOKEN-EXPIRED');
        if (isTokenExpired) {
          return refreshToken().then(() => {
            // original fetch after the token has been refreshed
            return resolve(baseFetcher(url, method, body, asJson, forceFetch));
          });
        } else {
          if (url.includes('/authenticate')) {
            // If call is of authenticate i.e login and still get 401, it means authentication is failed
            showAlertSnackBar(
              EAlertType.ERROR,
              ESnackBarPosition.TOP,
              'Authentication Failed!',
            );
            return;
          }
          clearUserDataAndLogout();
        }
      } else {
        await response
          .json()
          .then((resp) => reject(resp))
          .catch(() => reject(response));
      }
    } catch (error) {
      if (error instanceof TypeError) {
        reject(new NetworkError(error.message));
        return;
      }
      reject(error);
    }
  });
}

export async function fetcher(
  url,
  method,
  body,
  asJson = false,
  forceFetch = false,
  signal = null,
  retryCount = API_RETRY_COUNT,
  delayMs = API_RETRY_BASE_DELAY_MS,
) {
  try {
    return await baseFetcher(url, method, body, asJson, forceFetch, signal);
  } catch (error) {
    const isRetryable = shouldRetry(error);
    if (isRetryable && retryCount > 0) {
      const jitter = Math.random() * 500;
      const backoffDelay = delayMs + jitter;

      await new Promise((resolve) => setTimeout(resolve, backoffDelay));

      return fetcher(
        url,
        method,
        body,
        asJson,
        forceFetch,
        signal,
        retryCount - 1,
        delayMs * 2,
      );
    }

    throw error;
  }
}

const fetchData = async (url, body, method, signal) => {
  const headers = await getHeaders();
  const fetchOptions = {
    method: method || 'get',
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    credentials: 'include', // include, *same-origin, omit
    headers,
    redirect: 'follow', // manual, *follow, error
    referrerPolicy: 'no-referrer', // no-referrer, *client
    body: toJson(body),
  };
  if (signal) {
    fetchOptions.signal = signal;
  }
  return await fetch(url, fetchOptions);
};

export const refreshToken = async (redirectOnFailure = true) => {
  // Reuse existing in-progress refresh calls
  if (refreshTokenPromise) return refreshTokenPromise;

  refreshTokenPromise = refreshTokenInternal(redirectOnFailure)
    .then(async (response) => {
      if (!response) return;

      const result = await response.json();

      if (result.responseType === ResponseType.Success) {
        await CacheHelper.put('token', result.jwtToken);
        return result;
      }

      // Error response
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        result.responseMessage,
      );
      history.push('/logout');
    })
    .finally(() => {
      refreshTokenPromise = null;
    });

  return refreshTokenPromise;
};

const refreshTokenInternal = async (redirectOnFailure = true) => {
  const jwtToken = await CacheHelper.get('token');

  if (!jwtToken || jwtToken === 'undefined') {
    if (redirectOnFailure) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Cookies removed unexpectedly. Logging session out.',
      );
      history.push('/logout');
    }
    return null;
  }

  return fetchData(
    'api/Authentication/refresh',
    {
      jwtToken,
      refreshToken: '',
      responseMessage: '',
    },
    'POST',
  );
};

function toJson(str) {
  try {
    JSON.parse(str);
    return str;
  } catch (e) {
    return JSON.stringify(str);
  }
}

// generic hooks version for all request types
export function useRockFetch() {
  async function load(url, method, body, forceFetch = false, signal = null) {
    const response = await fetcher(url, method, body, true, forceFetch, signal);
    if (response) {
      //  const resp = await response.clone().json();
      return response;
    }
    return null;
  }
  return [load];
}

// DEPRECATED
export default function useRockFetchGet() {
  return useRockFetch();
}

// DEPRECATED
export function useRockFetchPost() {
  async function load(url, body, rawResponse = false) {
    const response = await fetcher(url, 'POST', body, false, true);
    if (response) {
      if (!rawResponse) {
        const resp = await response.clone().json();
        return resp;
      }
      {
        return response;
      }
    }
    return null;
  }
  return [load];
}
